import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const BillingQuickActions = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('actions'); // actions | stats
  const [stats, setStats] = useState({
    collection: 45200, pendingDues: 12400, billsToday: 38, avgBill: 1189
  });
  const loading = false;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency', currency: 'INR', minimumFractionDigits: 0
    }).format(amount);
  };

  const primaryActions = [
    { name: 'New Bill', path: '/billing/form', icon: 'bi-file-earmark-plus', shortcut: 'Ctrl+B', color: 'new-bill' },
    { name: 'Payment / Receipt', path: '/billing/payment', icon: 'bi-cash', shortcut: 'Ctrl+P', color: 'payment' },
    { name: 'Bill List', path: '/billing/list', icon: 'bi-list-ul', shortcut: 'Ctrl+I', color: 'bill-list' },
  ];

  const secondaryActions = [
    { name: 'Pending Dues', path: '/billing/due-list', icon: 'bi-journal-x', shortcut: 'Ctrl+D', color: 'due-list' },
    { name: 'Daily Summary', path: '/billing/summary', icon: 'bi-pie-chart', shortcut: 'Ctrl+R', color: 'summary' },
    { name: 'Payment Report', path: '/billing/payment-report', icon: 'bi-wallet2', color: 'pay-rep' },
    { name: 'General Report', path: '/billing/report', icon: 'bi-file-bar-graph', color: 'gen-rep' },
  ];

  return (
    <div className="qa-container">
      {/* Header with Tabs */}
      <div className="qa-header">
        <div className="qa-tabs">
          <button
            className={`qa-tab ${activeTab === 'actions' ? 'active' : ''}`}
            onClick={() => setActiveTab('actions')}
          >
            <i className="bi bi-lightning-charge-fill"></i> Actions
          </button>
          <button
            className={`qa-tab ${activeTab === 'stats' ? 'active' : ''}`}
            onClick={() => setActiveTab('stats')}
          >
            <i className="bi bi-bar-chart-fill"></i> Revenue
          </button>
        </div>
      </div>

      <div className="qa-body">
        {activeTab === 'actions' && (
          <>
            <div className="qa-section-label">
              <span>Primary Actions</span>
              <i className="bi bi-star-fill" style={{ color: '#f59e0b', fontSize: '10px' }}></i>
            </div>
            <div className="qa-group">
              {primaryActions.map((action, i) => (
                <button
                  key={i}
                  className={`erp-action-card ${action.color} primary-card`}
                  onClick={() => navigate(action.path)}
                >
                  <div className="icon-wrapper"><i className={`bi ${action.icon}`}></i></div>
                  <span className="card-text">{action.name}</span>
                  {action.shortcut && <span className="qa-key">{action.shortcut}</span>}
                </button>
              ))}
            </div>

            <div className="qa-section-label mt-3">
              <span>Reports & Dues</span>
            </div>
            <div className="qa-group compact">
              {secondaryActions.map((action, i) => (
                <button
                  key={i}
                  className={`erp-action-card ${action.color}`}
                  onClick={() => navigate(action.path)}
                >
                  <div className="icon-wrapper"><i className={`bi ${action.icon}`}></i></div>
                  <span className="card-text">{action.name}</span>
                  {action.shortcut && <span className="qa-key">{action.shortcut}</span>}
                </button>
              ))}
            </div>
          </>
        )}

        {activeTab === 'stats' && (
          <>
            <div className="qa-section-label">
              <span>Today's Revenue</span>
              <i className="bi bi-calendar-check" style={{ color: '#d97706', fontSize: '11px' }}></i>
            </div>
            <div className="erp-stats-grid">
              <div className="stat-box box-green">
                <i className="bi bi-wallet2 stat-bg-icon"></i>
                <div className="stat-label">Collection</div>
                <div className="stat-value">{loading ? '...' : formatCurrency(stats.collection)}</div>
              </div>
              <div className="stat-box box-red">
                <i className="bi bi-exclamation-circle stat-bg-icon"></i>
                <div className="stat-label">Pending Dues</div>
                <div className="stat-value">{loading ? '...' : formatCurrency(stats.pendingDues)}</div>
              </div>
              <div className="stat-box box-amber">
                <i className="bi bi-receipt stat-bg-icon"></i>
                <div className="stat-label">Bills Today</div>
                <div className="stat-value">{loading ? '...' : stats.billsToday}</div>
              </div>
              <div className="stat-box box-blue">
                <i className="bi bi-graph-up stat-bg-icon"></i>
                <div className="stat-label">Avg Bill</div>
                <div className="stat-value">{loading ? '...' : formatCurrency(stats.avgBill)}</div>
              </div>
            </div>

            <div className="qa-section-label mt-3">
              <span>Collection Progress</span>
            </div>
            <div className="perf-card">
              <div className="perf-row">
                <span className="perf-label">Collected vs Target</span>
                <span className="perf-value text-accent">72%</span>
              </div>
              <div className="perf-bar">
                <div className="perf-fill" style={{ width: '72%', background: '#d97706' }}></div>
              </div>
              <div className="perf-row mt-2">
                <span className="perf-label">Dues Recovered</span>
                <span className="perf-value text-success">58%</span>
              </div>
              <div className="perf-bar">
                <div className="perf-fill" style={{ width: '58%', background: '#16a34a' }}></div>
              </div>
            </div>
          </>
        )}
      </div>

      <style>{`
        .qa-container {
          --qa-accent: #d97706;
          --qa-accent-dark: #b45309;
          --qa-accent-soft: #fffbeb;
          --qa-accent-border: #fde68a;

          display: flex; flex-direction: column;
          height: 100%; background: #ffffff;
        }

        .qa-header {
          border-bottom: 1px solid #f1f5f9;
          background: linear-gradient(180deg, #ffffff 0%, #fafbfc 100%);
        }

        .qa-tabs {
          display: flex; padding: 8px 8px 0 8px;
          gap: 2px;
        }

        .qa-tab {
          flex: 1; background: transparent;
          border: none; padding: 9px 6px;
          font-size: 11px; font-weight: 700;
          color: #64748b; cursor: pointer;
          border-radius: 7px 7px 0 0;
          display: flex; align-items: center;
          justify-content: center; gap: 5px;
          position: relative; transition: all 0.2s;
        }
        .qa-tab i { font-size: 11px; }
        .qa-tab:hover { background: #f8fafc; color: #0f172a; }
        .qa-tab.active {
          background: var(--qa-accent-soft);
          color: var(--qa-accent-dark);
          border-bottom: 2px solid var(--qa-accent);
        }

        .qa-body {
          padding: 12px; overflow-y: auto; flex: 1;
        }
        .qa-body::-webkit-scrollbar { width: 5px; }
        .qa-body::-webkit-scrollbar-thumb {
          background: #cbd5e1; border-radius: 10px;
        }

        .qa-section-label {
          font-size: 10px; font-weight: 800;
          color: #94a3b8; text-transform: uppercase;
          letter-spacing: 0.6px; margin-bottom: 8px;
          display: flex; justify-content: space-between;
          align-items: center;
        }

        .qa-group {
          display: flex; flex-direction: column; gap: 6px;
        }
        .qa-group.compact { gap: 4px; }
        .qa-group.compact .erp-action-card { padding: 6px 10px; }
        .qa-group.compact .icon-wrapper { width: 28px; height: 28px; font-size: 12px; }

        .erp-action-card {
          display: flex; align-items: center;
          width: 100%; padding: 8px 12px;
          border: 1px solid #f1f5f9; border-radius: 9px;
          background: #ffffff;
          box-shadow: 0 1px 2px rgba(15, 23, 42, 0.03);
          cursor: pointer; transition: all 0.2s ease;
          text-align: left;
        }
        .primary-card { padding: 10px 12px; }
        .primary-card .icon-wrapper { width: 36px; height: 36px; font-size: 16px; }

        .erp-action-card:hover {
          transform: translateX(-2px);
          box-shadow: 0 4px 12px rgba(15, 23, 42, 0.08);
          border-color: #e2e8f0;
        }

        .icon-wrapper {
          width: 30px; height: 30px;
          display: flex; align-items: center;
          justify-content: center; border-radius: 8px;
          margin-right: 10px; font-size: 13px;
          transition: all 0.2s;
        }

        .card-text {
          font-size: 12.5px; font-weight: 600;
          color: #475569; flex: 1;
        }

        .qa-key {
          font-size: 10px; background: #f1f5f9;
          padding: 3px 6px; border-radius: 5px;
          color: #64748b; font-family: 'Consolas', monospace;
          font-weight: 600; border: 1px solid #e2e8f0;
          white-space: nowrap;
        }

        /* Billing action card colors */
        .new-bill .icon-wrapper { background: #fef3c7; color: #d97706; }
        .new-bill:hover { background: #fffbeb; border-color: #fde68a; }
        .new-bill:hover .card-text { color: #b45309; }

        .payment .icon-wrapper { background: #dcfce7; color: #16a34a; }
        .payment:hover { background: #f0fdf4; border-color: #bbf7d0; }
        .payment:hover .card-text { color: #166534; }

        .bill-list .icon-wrapper { background: #e0e7ff; color: #2563eb; }
        .bill-list:hover { background: #eff6ff; border-color: #bfdbfe; }
        .bill-list:hover .card-text { color: #1e40af; }

        .due-list .icon-wrapper { background: #fee2e2; color: #dc2626; }
        .due-list:hover { background: #fef2f2; border-color: #fecaca; }
        .due-list:hover .card-text { color: #991b1b; }

        .summary .icon-wrapper { background: #f3e8ff; color: #9333ea; }
        .summary:hover { background: #faf5ff; border-color: #e9d5ff; }
        .summary:hover .card-text { color: #6b21a8; }

        .pay-rep .icon-wrapper { background: #ccfbf1; color: #0d9488; }
        .pay-rep:hover { background: #f0fdfa; border-color: #99f6e4; }
        .pay-rep:hover .card-text { color: #0f766e; }

        .gen-rep .icon-wrapper { background: #ffedd5; color: #ea580c; }
        .gen-rep:hover { background: #fff7ed; border-color: #fed7aa; }
        .gen-rep:hover .card-text { color: #c2410c; }

        /* Stats */
        .erp-stats-grid {
          display: grid; grid-template-columns: 1fr 1fr; gap: 8px;
        }
        .stat-box {
          background: #ffffff; border-radius: 9px;
          padding: 11px; position: relative;
          overflow: hidden;
          box-shadow: 0 1px 2px rgba(15, 23, 42, 0.03);
          border: 1px solid #e2e8f0;
        }
        .stat-bg-icon {
          position: absolute; right: -5px; bottom: -10px;
          font-size: 40px; opacity: 0.05;
        }
        .stat-label {
          font-size: 10px; color: #64748b;
          font-weight: 700; margin-bottom: 3px;
          text-transform: uppercase; letter-spacing: 0.3px;
        }
        .stat-value { font-size: 14px; font-weight: 800; }

        .box-green { border-left: 4px solid #10b981; }
        .box-green .stat-value { color: #059669; }
        .box-red { border-left: 4px solid #ef4444; }
        .box-red .stat-value { color: #dc2626; }
        .box-amber { border-left: 4px solid #f59e0b; }
        .box-amber .stat-value { color: #d97706; }
        .box-blue { border-left: 4px solid #3b82f6; }
        .box-blue .stat-value { color: #2563eb; }

        .perf-card {
          background: #f8fafc; border: 1px solid #f1f5f9;
          border-radius: 9px; padding: 12px;
        }
        .perf-row {
          display: flex; justify-content: space-between;
          font-size: 11px; font-weight: 600;
          margin-bottom: 4px;
        }
        .perf-label { color: #64748b; }
        .perf-value { font-weight: 700; }
        .text-success { color: #16a34a; }
        .text-accent { color: #d97706; }
        .perf-bar {
          height: 6px; background: #e2e8f0;
          border-radius: 10px; overflow: hidden;
        }
        .perf-fill {
          height: 100%; border-radius: 10px;
          transition: width 1s ease;
        }
      `}</style>
    </div>
  );
};

export default BillingQuickActions;